import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Body } from "@hypercard/web-components";
import { Check, Plus, X } from "@phosphor-icons/react";
import { cn, getInitialsFromEmployee } from "@/utils/utils";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  CurrentUserDocument,
  Employee,
  useActiveEmployeeProfileRecommendationsQuery,
  useUpdateCurrentUserEmployeeDelegatesMutation,
} from "@common/utils/graphql";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useEffect, useState } from "react";
import { useDelegateEmployees } from "./useDelegateEmployees";

type SelectInputProps = {
  selected: Employee[];
  setSelected: (
    employees: Employee[] | ((prev: Employee[]) => Employee[]),
  ) => void;
};
const SelectInput = ({ selected, setSelected }: SelectInputProps) => {
  const [open, setOpen] = useState(false);
  const [searchText, setSearchText] = useState("");

  const { data, loading } = useActiveEmployeeProfileRecommendationsQuery({
    variables: {
      offset: 0,
      pageSize: 10,
      searchText:
        searchText.length >= 0 && searchText.length < 3 ? null : searchText,
    },
  });

  const employees =
    data?.activeEmployeeProfileRecommendations.__typename ===
    "QueryActiveEmployeeProfileRecommendationsSuccess"
      ? data.activeEmployeeProfileRecommendations.data.employees
      : [];

  const handleSelectEmployee = (workEmail: string) => {
    const filteredEmployees = selected.filter(
      (employee) => workEmail !== employee.workEmail,
    );
    if (filteredEmployees.length === selected.length) {
      const employee = employees.find(
        (employee) => employee.workEmail === workEmail,
      );
      if (employee) {
        setSelected((prev) => [...prev, employee]);
      }
    } else {
      setSelected(filteredEmployees);
    }
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="flex w-full items-center justify-between border-none bg-[#1c1c1c] text-[#a1a1aa]"
          >
            <Body size="md" weight="regular">
              Search for people
            </Body>
            <Plus className="ml-2 size-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[335px] p-0">
          <Command>
            <CommandInput
              onValueChange={setSearchText}
              value={searchText}
              placeholder="Search employees"
            />
            <CommandEmpty>
              <div>
                {loading ? (
                  <Body size="md" weight="regular">
                    Searching...
                  </Body>
                ) : 0 < searchText.length && searchText.length < 3 ? (
                  <Body size="md" weight="regular">
                    Type in at least three characters to search
                  </Body>
                ) : (
                  <Body size="md" weight="regular">
                    No employees found
                  </Body>
                )}
              </div>
            </CommandEmpty>
            <CommandGroup>
              {searchText.length < 3 && employees.length > 0 && (
                <Body size="md" weight="regular" className="p-3">
                  Recommended employees
                </Body>
              )}
              {searchText.length >= 0 &&
                employees.map((employee) => (
                  <CommandItem
                    key={employee.workEmail}
                    value={employee.workEmail}
                    onSelect={handleSelectEmployee}
                  >
                    <div className="flex w-full justify-between">
                      <div className="flex flex-row">
                        <Avatar>
                          <AvatarFallback>
                            {getInitialsFromEmployee(employee)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex flex-col pl-2">
                          <div>
                            {employee.firstName} {employee.lastName}
                          </div>
                          <div>{employee.workEmail}</div>
                        </div>
                      </div>
                      <div className="flex-end flex items-center justify-end">
                        <Check
                          className={cn(
                            "w-100 mr-2 size-4",
                            selected.find(
                              (e) => e.workEmail === employee.workEmail,
                            )
                              ? "opacity-100"
                              : "opacity-0",
                          )}
                        />
                      </div>
                    </div>
                  </CommandItem>
                ))}
            </CommandGroup>
          </Command>
        </PopoverContent>
      </Popover>
      <ul className="mt-6 h-28 w-full overflow-auto scrollbar-thin">
        {selected.length === 0 && (
          <Body
            size="md"
            weight="regular"
            className="w-full text-center text-white/70"
          >
            No delegates added
          </Body>
        )}
        {selected.map((selectedEmployee) => {
          return (
            <li
              className="flex w-full justify-between space-x-4 pt-4"
              key={selectedEmployee.id}
            >
              <div className="flex flex-row items-center">
                <Avatar>
                  <AvatarFallback>
                    {getInitialsFromEmployee(selectedEmployee)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex flex-col pl-2">
                  <div>
                    {selectedEmployee.firstName} {selectedEmployee.lastName}
                  </div>
                  <p className="text-slate">{selectedEmployee.workEmail}</p>
                </div>
              </div>
              <div className="flex-end flex items-center justify-end">
                <X
                  onClick={() =>
                    handleSelectEmployee(selectedEmployee.workEmail)
                  }
                  className={cn(
                    "w-100 mr-2 h-4 w-4 cursor-pointer opacity-100",
                  )}
                />
              </div>
            </li>
          );
        })}
      </ul>
    </>
  );
};

export const AddDelegateInput = () => {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<Employee[]>([]);

  const { delegateEmployees } = useDelegateEmployees();
  const [updateCurrentUserEmployeeDelegates] =
    useUpdateCurrentUserEmployeeDelegatesMutation({
      refetchQueries: [CurrentUserDocument],
    });

  useEffect(() => {
    setSelected(delegateEmployees);
  }, [delegateEmployees, setSelected]);

  const handleConfirm = () => {
    const delegateEmployeeProfileIds = selected.map((e) => e.id) as string[];
    updateCurrentUserEmployeeDelegates({
      variables: { delegateEmployeeProfileIds },
    });
  };

  return (
    <Dialog onOpenChange={setOpen}>
      <ul className="flex flex-col">
        {delegateEmployees.map((u) => (
          <li key={u.userId}>
            <Body size="lg" weight="regular" className="text-white/70">
              {u.firstName} {u.lastName}
            </Body>
          </li>
        ))}
      </ul>
      <DialogTrigger asChild>
        <div className="flex items-center">
          <Body size="lg" weight="regular">
            Add Delegate
          </Body>
          <Plus className="ml-1.5 size-4 text-white" />
        </div>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add or remove delegate</DialogTitle>
        </DialogHeader>
        <Body
          size="sm"
          weight="regular"
          className="mb-1 mt-4 self-start text-white/70"
        >
          Delegate
        </Body>
        {open && <SelectInput selected={selected} setSelected={setSelected} />}
        <DialogFooter className="mt-10 gap-4">
          <DialogClose asChild>
            <Button type="button" variant="ghost" className="min-w-36">
              Cancel
            </Button>
          </DialogClose>
          <DialogClose asChild>
            <Button
              type="button"
              variant="default"
              onClick={handleConfirm}
              className="min-w-36"
            >
              Confirm
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
